import { useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Button,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { makeStyles } from '@material-ui/core/styles';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntityFilters, type EntityFilters } from '../hooks/useEntityFilters';
import { useGrafanaQuery } from '../hooks/useGrafanaQuery';
import { useGrafanaAlerts } from '../hooks/useGrafanaAlerts';
import { useLokiLogs } from '../hooks/useLokiLogs';
import {
  buildServiceOverviewQueries,
  SERVICE_OVERVIEW_PANELS,
} from '../queries/serviceOverview';
import { buildHttpTrafficQueries, HTTP_PANELS } from '../queries/httpTraffic';
import {
  transformToTimeSeries,
  transformToInstantValue,
  transformToLabeledValues,
} from '../utils/transformDataFrame';
import { StatCard } from './charts/StatCard';
import { TimeSeriesChart } from './charts/TimeSeriesChart';
import { DonutChart } from './charts/DonutChart';
import { AlertsPanel } from './AlertsPanel';
import { LogsPanel } from './LogsPanel';
import { LoadTestPanel } from './LoadTestPanel';

const useStyles = makeStyles(theme => ({
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(1, 0),
  },
  grafanaLink: {
    marginLeft: 'auto',
    textTransform: 'none',
    fontSize: 12,
  },
}));

interface NativeDashboardContentProps {
  filters?: EntityFilters;
}

export const NativeDashboardContent = ({ filters: filtersProp }: NativeDashboardContentProps) => {
  const classes = useStyles();
  const ownFilters = useEntityFilters();
  const filters = filtersProp ?? ownFilters;
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);

  // Build Grafana dashboard link from entity annotation
  const grafanaUrl = useMemo(() => {
    const domain = configApi.getOptionalString('grafana.domain') ?? '';
    const selector =
      entity.metadata.annotations?.['grafana/dashboard-selector'] ?? '';
    const uidMatch = selector.match(/uid\s*==\s*'([^']+)'/);
    if (domain && uidMatch) {
      return `${domain}/d/${uidMatch[1]}`;
    }
    return domain ? `${domain}/dashboards` : '';
  }, [configApi, entity]);

  // ── Alerts ────────────────────────────────────────────────
  const { alerts, loading: alertsLoading } = useGrafanaAlerts();

  // ── Logs ──────────────────────────────────────────────────
  const {
    entries: logEntries,
    loading: logsLoading,
    refresh: refreshLogs,
    pods: logPods,
  } = useLokiLogs({
    service: filters.selectedService,
    timeRange: filters.timeRange,
  });
  const [selectedPod, setSelectedPod] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');

  // ── Build queries ─────────────────────────────────────────
  const overviewQueries = useMemo(
    () =>
      buildServiceOverviewQueries(
        filters.selectedService,
        filters.activePreset ?? '1h',
      ),
    [filters.selectedService, filters.activePreset],
  );

  const httpQueries = useMemo(
    () =>
      buildHttpTrafficQueries(
        filters.selectedService,
        filters.activePreset ?? '1h',
      ),
    [filters.selectedService, filters.activePreset],
  );

  // ── Execute queries ───────────────────────────────────────
  const overview = useGrafanaQuery(overviewQueries, filters.timeRange);
  const http = useGrafanaQuery(httpQueries, filters.timeRange);

  // ── Transform data ────────────────────────────────────────
  const statValues = useMemo(() => {
    if (!overview.data) return {};
    const result: Record<string, number | null> = {};
    for (const panel of SERVICE_OVERVIEW_PANELS) {
      result[panel.refId] = transformToInstantValue(overview.data, panel.refId);
    }
    return result;
  }, [overview.data]);

  const statSparklines = useMemo(() => {
    if (!overview.data) return {};
    const result: Record<string, { time: number; value: number }[]> = {};
    for (const panel of SERVICE_OVERVIEW_PANELS) {
      const series = transformToTimeSeries(overview.data, panel.refId);
      result[panel.refId] = series[0]?.points ?? [];
    }
    return result;
  }, [overview.data]);

  const httpRequestRateSeries = useMemo(
    () => (http.data ? transformToTimeSeries(http.data, HTTP_PANELS.requestRate.refId, '{{ service_name }}') : []),
    [http.data],
  );

  const httpLatencySeries = useMemo(() => {
    if (!http.data) return [];
    return [
      ...transformToTimeSeries(http.data, HTTP_PANELS.latency.refIds.p50, 'p50 — {{ service_name }}'),
      ...transformToTimeSeries(http.data, HTTP_PANELS.latency.refIds.p95, 'p95 — {{ service_name }}'),
      ...transformToTimeSeries(http.data, HTTP_PANELS.latency.refIds.p99, 'p99 — {{ service_name }}'),
    ];
  }, [http.data]);

  const httpErrorSeries = useMemo(
    () => (http.data ? transformToTimeSeries(http.data, HTTP_PANELS.errorRate.refId, '{{ service_name }}') : []),
    [http.data],
  );

  const httpStatusData = useMemo(
    () =>
      http.data
        ? transformToLabeledValues(http.data, HTTP_PANELS.statusCodes.refId, HTTP_PANELS.statusCodes.legendFormat)
        : [],
    [http.data],
  );

  return (
    <Box>
      {/* ── Filter Bar ─────────────────────────────────── */}
      <Box className={classes.filterBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Service</InputLabel>
          <Select
            value={filters.selectedService}
            onChange={e => filters.setSelectedService(e.target.value as string)}
            label="Service"
          >
            <MenuItem value=".+">All</MenuItem>
            {filters.services.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {grafanaUrl && (
          <Button
            className={classes.grafanaLink}
            size="small"
            variant="outlined"
            href={grafanaUrl}
            target="_blank"
            rel="noopener"
            endIcon={<OpenInNewIcon fontSize="small" />}
          >
            Open in Grafana
          </Button>
        )}
      </Box>

      {/* ── Alerts ─────────────────────────────────────── */}
      <InfoCard title="Alerts" noPadding>
        <AlertsPanel alerts={alerts} loading={alertsLoading} />
      </InfoCard>

      {/* ── Service Overview Stats ─────────────────────── */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 16 }}>
        Service Overview
      </Typography>
      <Grid container spacing={2}>
        {SERVICE_OVERVIEW_PANELS.map(panel => (
          <Grid item xs={6} sm={4} md={2} key={panel.refId}>
            <StatCard
              title={panel.title}
              value={statValues[panel.refId] ?? null}
              unit={panel.unit}
              thresholds={panel.thresholds}
              loading={overview.loading}
              sparklineData={statSparklines[panel.refId]}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── HTTP Traffic ───────────────────────────────── */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
        HTTP Traffic
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoCard title={HTTP_PANELS.requestRate.title} noPadding>
            <Box p={1}>
              <TimeSeriesChart
                series={httpRequestRateSeries}
                unit={HTTP_PANELS.requestRate.unit}
                loading={http.loading}
              />
            </Box>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard title={HTTP_PANELS.latency.title} noPadding>
            <Box p={1}>
              <TimeSeriesChart
                series={httpLatencySeries}
                unit={HTTP_PANELS.latency.unit}
                loading={http.loading}
              />
            </Box>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard title={HTTP_PANELS.errorRate.title} noPadding>
            <Box p={1}>
              <TimeSeriesChart
                series={httpErrorSeries}
                unit={HTTP_PANELS.errorRate.unit}
                thresholdLine={HTTP_PANELS.errorRate.thresholdLine}
                loading={http.loading}
              />
            </Box>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard title={HTTP_PANELS.statusCodes.title} noPadding>
            <Box p={2}>
              <DonutChart
                data={httpStatusData}
                loading={http.loading}
              />
            </Box>
          </InfoCard>
        </Grid>
      </Grid>

      {/* ── Load Testing Controls ──────────────────────── */}
      {entity.metadata.annotations?.['k6/enabled'] === 'true' && (
        <LoadTestPanel entity={entity} />
      )}

      {/* ── Logs ───────────────────────────────────────── */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
        Application Logs
      </Typography>
      <InfoCard title="Loki Logs" noPadding>
        <Box p={1}>
          <LogsPanel
            entries={logEntries}
            loading={logsLoading}
            onRefresh={refreshLogs}
            activePreset={filters.activePreset ?? '1h'}
            timeRangePresets={filters.timeRangePresets}
            onTimeRangeChange={filters.setTimeRangePreset}
            pods={logPods}
            selectedPod={selectedPod}
            onPodChange={setSelectedPod}
            selectedLevel={selectedLevel}
            onLevelChange={setSelectedLevel}
          />
        </Box>
      </InfoCard>
    </Box>
  );
};
